import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Sparkles, DollarSign, Gift, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick Search Suggestions
const QUICK_SUGGESTIONS = [
  { label: "Summer faith tees", icon: "☀️" },
  { label: "Cozy hoodies for winter", icon: "❄️" },
  { label: "Gift for baptism", icon: "🎁" },
  { label: "Modest swimwear", icon: "🏊" },
  { label: "Family matching outfits", icon: "👨‍👩‍👧‍👦" },
  { label: "Scripture jewelry", icon: "✝️" }
];

// Product Card
const FinderProductCard = ({ product }) => (
  <Link
    to={`/products/${product.id || product.name?.toLowerCase().replace(/\s+/g, '-')}`}
    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
    data-testid="finder-product"
  >
    <div className="relative aspect-square overflow-hidden">
      <img
        src={product.images?.[0] || 'https://via.placeholder.com/300'}
        alt={product.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-pink-500 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Match
      </div>
    </div>
    <div className="p-3">
      <p className="text-xs text-purple-600 mb-1">{product.category}</p>
      <h3 className="font-medium text-gray-900 text-sm truncate">{product.name}</h3>
      <p className="text-amber-600 font-bold mt-1">${product.price?.toFixed(2)}</p>
    </div>
  </Link>
);

const AishaSmartFinder = () => {
  const { pathname } = useLocation();
  if (['/voyage', '/', '/selah', '/library'].includes(pathname)) return null;
  return <AishaSmartFinderInner />;
};

const AishaSmartFinderInner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [budget, setBudget] = useState('');
  const [occasion, setOccasion] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setQuery(searchQuery);
    
    try {
      const response = await fetch(`${API_URL}/api/aisha-public/smart-find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: searchQuery,
          budget: budget ? parseFloat(budget) : null,
          occasion: occasion || null
        })
      });
      
      const data = await response.json();
      setResults(data);
    } catch (error) {
      console.error('Smart find failed:', error);
    }
    
    setLoading(false);
  };
  
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };
  
  const clearResults = () => {
    setResults(null);
    setQuery('');
    setBudget('');
    setOccasion('');
  };
  
  return (
    <>
      {/* Trigger Button - Floating */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-4 sm:left-6 z-40 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-3.5 sm:px-5 sm:py-3 shadow-xl hover:shadow-purple-500/50 transition-all flex items-center gap-2"
        data-testid="smart-finder-button"
        aria-label="Smart Find"
      >
        <Search className="w-5 h-5" />
        <span className="font-medium hidden sm:inline">Smart Find</span>
      </button>
      
      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl" data-testid="smart-finder-modal">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 p-6 text-white relative">
              <button 
                onClick={() => { setIsOpen(false); clearResults(); }}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">A.I_SHA Smart Finder</h2>
                  <p className="text-sm text-white/80">Describe what you are looking for</p>
                </div>
              </div>
              
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g., cozy faith hoodie for Sunday service..."
                  className="w-full bg-white/20 border border-white/30 rounded-full px-5 py-3 text-white placeholder-white/60 focus:outline-none focus:bg-white/30 pr-12"
                  data-testid="smart-finder-input"
                />
                <button 
                  onClick={() => handleSearch()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white text-purple-600 rounded-full p-2 hover:bg-purple-100 transition-colors"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>
              
              {/* Filters */}
              <div className="flex gap-3 mt-4">
                <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                  <DollarSign className="w-4 h-4" />
                  <input
                    type="number"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    placeholder="Max budget"
                    className="bg-transparent w-24 text-sm focus:outline-none placeholder-white/60"
                  />
                </div>
                <select
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  className="bg-white/20 border border-white/30 rounded-full px-4 py-2 text-sm focus:outline-none"
                >
                  <option value="">Any occasion</option>
                  <option value="church">Church/Sunday Service</option>
                  <option value="casual">Casual Everyday</option>
                  <option value="gift">Gift Giving</option>
                  <option value="vacation">Vacation/Travel</option>
                  <option value="special">Special Event</option>
                </select>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Quick Suggestions */}
              {!results && !loading && (
                <div>
                  <p className="text-sm text-gray-500 mb-3">Try searching for:</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_SUGGESTIONS.map((suggestion) => (
                      <button
                        key={suggestion.label}
                        onClick={() => handleSuggestionClick(suggestion.label)}
                        className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-full px-4 py-2 text-sm transition-colors"
                      >
                        <span>{suggestion.icon}</span>
                        {suggestion.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Loading */}
              {loading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 flex items-center justify-center animate-pulse">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <p className="mt-4 text-gray-600">A.I_SHA is finding perfect matches...</p>
                </div>
              )}
              
              {/* Results */}
              {results && !loading && (
                <div>
                  {/* A.I_SHA's Recommendation */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 mb-6 border border-purple-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">{results.recommendation}</p>
                        <p className="text-xs text-purple-500 mt-1">Found {results.total_found} matches</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Products Grid */}
                  {results.products?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {results.products.map((product) => (
                        <FinderProductCard key={product.id || product.name} product={product} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No exact matches found. Try a different description!</p>
                    </div>
                  )}
                  
                  {/* See All */}
                  <div className="text-center mt-6">
                    <Link 
                      to={`/products?search=${encodeURIComponent(query)}`}
                      onClick={() => setIsOpen(false)}
                    >
                      <Button className="bg-purple-600 hover:bg-purple-700 rounded-full">
                        Browse All Results
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AishaSmartFinder;
